import { Component, computed, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { GlobalsStore } from '../../../stores/globals.state';
import { Router } from '@angular/router';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import { DataLayerStore } from '../../../stores/data-layer/data-layer.state';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';
import { IndicatorStore } from '../../../stores/indicator/indicator.state';
import { BookmarkStore } from '../../../stores/bookmark/bookmark.state';
import { getState } from '@ngrx/signals';
import { BookmarkValidationService } from '../../../services/bookmark-validation.service';

@Component({
  selector: 'app-validation-badge',
  standalone: true,
  imports: [CommonModule, BadgeModule, ButtonModule, TooltipModule],
  template: `
    @if (activeUserStore.canAccessBookmarks()) {
      <p-button
        size="small"
        styleClass="relative overflow-visible"
        [badge]="
          invalidBookmarksCount() > 0 ? invalidBookmarksCount().toString() : '✓'
        "
        [badgeClass]="
          (invalidBookmarksCount() > 0 ? 'p-badge-danger' : 'p-badge-success') +
          ' absolute top-0 right-0 m-0 -mt-1 -mr-1' +
          (invalidBookmarksCount() > 9 ? ' px-1' : '')
        "
        icon="fas fa-note-sticky"
        [rounded]="true"
        [pTooltip]="tooltipText()"
        tooltipPosition="bottom"
        (onClick)="openValidation()" />
    }
  `,
  styles: [
    `
      :host {
        display: inline-block;
      }
    `,
  ],
})
export class ValidationBadgeComponent {
  readonly globalsStore = inject(GlobalsStore);
  readonly activeUserStore = inject(ActiveUserStore);
  readonly dataLayerStore = inject(DataLayerStore);
  readonly indicatorDataStore = inject(IndicatorDataStore);
  readonly indicatorStore = inject(IndicatorStore);
  readonly bookmarkStore = inject(BookmarkStore);

  readonly validationService = inject(BookmarkValidationService);
  readonly router = inject(Router);

  constructor() {
    effect(() => {
      // re-validate the views whenever something they depend on changed:
      // years, indicator data, indicators (e.g. deleted) or the views
      const triggers = [
        getState(this.dataLayerStore).trigger,
        getState(this.indicatorDataStore).trigger,
        getState(this.indicatorStore).trigger,
        getState(this.bookmarkStore).trigger,
      ];

      if (
        triggers.some(trigger => trigger?.includes('runBookmarkValidation'))
      ) {
        setTimeout(async () => {
          const summary =
            await this.validationService.validateBookmarksWithServer();
          this.globalsStore.setBookmarkValidation(summary);
        }, 500);
      }
    });
  }

  validationSummary = computed(() => this.globalsStore.bookmarkValidation());

  invalidBookmarksCount = computed(
    () => this.validationSummary()?.invalidBookmarks || 0
  );

  tooltipText = computed(() => {
    const summary = this.validationSummary();
    if (!summary) return '';

    const bookmarks = summary.invalidBookmarks;
    const issues = summary.totalIssues;

    if (bookmarks === 0) return 'Keine fehlerhaften Ansichten';
    return `${bookmarks} fehlerhafte Ansicht${bookmarks !== 1 ? 'en' : ''} mit ${issues} Problem${issues !== 1 ? 'en' : ''}`;
  });

  openValidation(): void {
    this.router.navigate([
      '/data-backend',
      {
        outlets: {
          overlay: [
            'lesezeichen',
            { outlets: { bearbeiten: ['validierung', 'list'] } },
          ],
        },
      },
    ]);
  }
}
